import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const GlowProgressRing = ({ completed, total, size = 80 }) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;
  const filterId = `glow-lime-today`;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
        <circle
          cx={center} cy={center} r={radius} fill="none"
          stroke="#AAFF00" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          filter={`url(#${filterId})`}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif", fontWeight: 800, fontSize: '1rem', color: '#AAFF00', textShadow: '0 0 12px #AAFF0088', lineHeight: 1 }}>
          {percentage}%
        </span>
      </div>
    </div>
  );
};

const TodayWorkoutCard = ({ todayWorkout, completions, onToggleCompletion, isLoading }) => {
  const [expandedExercise, setExpandedExercise] = useState(null);

  if (isLoading) {
    return (
      <div className="rounded-2xl p-6 animate-pulse" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
        <div className="h-6 rounded w-1/3 mb-4" style={{ background: 'rgba(255,255,255,0.08)' }} />
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => <div key={i} className="h-16 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)' }} />)}
        </div>
      </div>
    );
  }

  if (!todayWorkout) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <Icon name="Calendar" size={28} style={{ color: 'rgba(255,255,255,0.3)' }} />
        </div>
        <h3 className="text-lg font-bold mb-2" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Rest Day</h3>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>No workout scheduled for today. Enjoy your recovery!</p>
      </div>
    );
  }

  const exercises = todayWorkout?.workoutPlanExercises || [];
  const completedCount = exercises?.filter(ex => completions?.[ex?.id]?.completed)?.length;
  const totalCount = exercises?.length;
  const isComplete = completedCount === totalCount && totalCount > 0;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
      {/* Header */}
      <div
        className="p-5 border-b"
        style={{
          background: 'linear-gradient(135deg, rgba(170,255,0,0.06) 0%, rgba(0,212,255,0.06) 100%)',
          borderColor: 'rgba(255,255,255,0.07)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(170,255,0,0.15)', border: '1px solid rgba(170,255,0,0.3)' }}>
                <Icon name="Dumbbell" size={14} style={{ color: '#AAFF00' }} />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#AAFF00', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Today's Workout</span>
            </div>
            <h3 className="text-lg font-extrabold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif", letterSpacing: '-0.02em' }}>
              {todayWorkout?.dayLabel || 'Workout'}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{todayWorkout?.planName}</p>
          </div>
          <div className="flex items-center gap-3">
            <GlowProgressRing completed={completedCount} total={totalCount} size={72} />
            {isComplete && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: 'rgba(170,255,0,0.1)', border: '1px solid rgba(170,255,0,0.3)', boxShadow: '0 0 12px rgba(170,255,0,0.2)' }}
              >
                <Icon name="Trophy" size={14} style={{ color: '#AAFF00' }} />
                <span className="text-xs font-bold" style={{ color: '#AAFF00' }}>Complete!</span>
              </motion.div>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{completedCount}/{totalCount} exercises</span>
        </div>
      </div>
      {/* Exercise List */}
      <div className="p-4 space-y-2">
        {exercises?.map((ex, idx) => {
          const isCompleted = completions?.[ex?.id]?.completed;
          const isExpanded = expandedExercise === ex?.id;

          return (
            <motion.div
              key={ex?.id}
              layout
              className="rounded-xl transition-all"
              style={{
                border: isCompleted ? '1px solid rgba(170,255,0,0.25)' : '1px solid rgba(255,255,255,0.07)',
                background: isCompleted ? 'rgba(170,255,0,0.05)' : 'rgba(255,255,255,0.03)',
              }}
            >
              <div className="flex items-center gap-3 p-3">
                {/* Checkbox */}
                <button
                  onClick={() => onToggleCompletion(ex?.id, !isCompleted)}
                  className="flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all"
                  style={{
                    background: isCompleted ? '#AAFF00' : 'transparent',
                    borderColor: isCompleted ? '#AAFF00' : 'rgba(255,255,255,0.2)',
                    boxShadow: isCompleted ? '0 0 8px rgba(170,255,0,0.4)' : 'none',
                  }}
                >
                  {isCompleted && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                      <Icon name="Check" size={12} color="#000" />
                    </motion.div>
                  )}
                </button>

                {/* Exercise number */}
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,212,255,0.12)' }}>
                  <span className="text-xs font-bold" style={{ color: '#00D4FF' }}>{idx + 1}</span>
                </div>

                {/* Exercise info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold transition-all" style={{ color: isCompleted ? 'rgba(255,255,255,0.3)' : '#fff', textDecoration: isCompleted ? 'line-through' : 'none', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>
                    {ex?.exercise?.name}
                  </p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {ex?.sets} sets × {ex?.reps} reps · {ex?.rest_seconds}s rest
                  </p>
                </div>

                {/* Expand button */}
                <button
                  onClick={() => setExpandedExercise(isExpanded ? null : ex?.id)}
                  className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                  style={{ color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.04)' }}
                >
                  <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
              </div>
              {/* Expanded details */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pt-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      {ex?.exercise?.description && (
                        <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.4)' }}>{ex?.exercise?.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {ex?.exercise?.muscle_groups?.map((m, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF' }}>{m}</span>
                        ))}
                        {ex?.exercise?.equipment_needed?.map((e, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>{e}</span>
                        ))}
                      </div>
                      {ex?.notes && (
                        <p className="text-xs mt-2 italic" style={{ color: 'rgba(255,255,255,0.35)' }}>Note: {ex?.notes}</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default TodayWorkoutCard;
